import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { Card, EmptyState, formatCurrency, formatInteger, LoadingBlock } from '@/design'
import type { Environment } from '@/lib/api'
import { usePageFreshness } from '@/lib/freshness'

interface Props {
  environments: readonly Environment[]
}

/**
 * Daily rollups over the last 30 days, the one analytics endpoint on `main`.
 * It proves the environment contract end to end: the scope shown is the scope
 * the response says it was computed over, never the app's own idea of it.
 */
export function DailyMetricsPanel({ environments }: Props) {
  const client = useApiClient()
  const metrics = useQuery({
    queryKey: ['daily-metrics', client.kind, [...environments].sort()],
    queryFn: () => client.dailyMetrics(environments),
  })
  // Daily metrics are a real endpoint: live from the HTTP client, demo from the mock.
  usePageFreshness(metrics.data ? { source: client.kind === 'http' ? 'live' : 'demo', lastUpdatedAt: null } : undefined)

  if (metrics.isPending) {
    return (
      <Card title="Last 30 days">
        <LoadingBlock />
      </Card>
    )
  }
  if (metrics.isError) {
    return (
      <Card title="Last 30 days">
        <EmptyState variant="outage" compact description="Could not load metrics." />
      </Card>
    )
  }

  const totals = metrics.data.rows.reduce(
    (accumulator, row) => ({
      sessions: accumulator.sessions + row.sessions,
      orders: accumulator.orders + row.orders,
      revenue: accumulator.revenue + row.gross_revenue,
    }),
    { sessions: 0, orders: 0, revenue: 0 },
  )
  const currency = metrics.data.rows[0]?.currency ?? 'GBP'

  return (
    <Card title="Last 30 days" subtitle={`Daily rollups · ${metrics.data.environments.join(', ')}`}>
      <section aria-label="Daily metrics" data-non-production={metrics.data.non_production}>
        <dl className="ci-stat-row">
          <div>
            <dt>Sessions</dt>
            <dd data-testid="total-sessions">{formatInteger(totals.sessions)}</dd>
          </div>
          <div>
            <dt>Orders</dt>
            <dd data-testid="total-orders">{formatInteger(totals.orders)}</dd>
          </div>
          <div>
            <dt>Gross revenue</dt>
            <dd data-testid="total-revenue">{formatCurrency(totals.revenue, { currency })}</dd>
          </div>
        </dl>
      </section>
    </Card>
  )
}
